// lib/use-is-subscribed.ts — auth-required-mode implementation of
// the subscription check. Reads the user's subscription status from
// `GET /me/subscription` (which the backend derives from Clerk
// `publicMetadata.subscription`).
//
// Why an endpoint instead of reading publicMetadata.subscription
// directly:
//   useUser() caches user metadata in the session JWT, and Clerk only
//   re-issues the JWT on its own cadence (typically minutes after a
//   metadata write). The endpoint goes through the Backend SDK and
//   reads live metadata, so an admin flipping the flag in the Clerk
//   dashboard is reflected on the user's next /me/subscription call —
//   which the waiting page polls every 30s.
//
// Returns a quad-state so the gate can distinguish "still loading"
// (show spinner), "loaded but inactive" (redirect to
// /waiting-for-activation), "loaded and active" (let through), and
// "errored" (show the actual API error — collapsing this into
// 'inactive' would silently send users to the waiting page for every
// backend blip, which is the bug fixed here).
import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { type ApiError, apiRequest } from "@/lib/api-client";

const subscriptionResponseSchema = z.object({
  data: z.object({ subscription: z.boolean() }),
});

export type SubscriptionStatus = "loading" | "inactive" | "active" | "error";

export type SubscriptionQueryOpts = {
  /** Polling cadence in ms. False disables polling. Default: no polling. */
  refetchInterval?: number | false;
};

export type UseSubscriptionStatusResult =
  | { status: "loading" }
  | { status: "active" }
  | { status: "inactive" }
  | { status: "error"; error: ApiError | Error; refetch: () => void };

const SUBSCRIPTION_STALE_MS = 30 * 1000;

// ponytail: staleTime matches the polling cadence on
// /waiting-for-activation so the page doesn't refetch on every focus
// tick. The auth-required layout redirects to the waiting page when
// the status resolves to 'inactive'; the page mounts the same query
// with refetchInterval=30s to drive automatic re-admission once the
// admin flips the flag.
//
// retry: false — the layout is a gate, not a background sync. The
// default 3-attempt backoff would have the user staring at a spinner
// for ~7s while the API is provably down. Fail fast, surface the
// error, let the user click "Tentar novamente".
export function useSubscriptionStatus(
  opts: SubscriptionQueryOpts = {},
): UseSubscriptionStatusResult {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const query = useQuery({
    queryKey: ["me", "subscription"] as const,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      // ponytail: bail on the network call when not signed in — Clerk
      // is still resolving the session, or the user signed out, and
      // /me/subscription would 401 anyway. Returning a synthetic
      // inactive response keeps the layout from briefly flashing the
      // app shell before the redirect lands.
      if (!isSignedIn) return { data: { subscription: false } };
      const token = await getToken();
      if (!token) return { data: { subscription: false } };
      const raw = await apiRequest<unknown>("/v2/me/subscription", { token, signal });
      return subscriptionResponseSchema.parse(raw);
    },
    enabled: isLoaded,
    staleTime: SUBSCRIPTION_STALE_MS,
    refetchInterval: opts.refetchInterval,
    retry: false,
  });
  if (query.isLoading) return { status: "loading" };
  if (query.isError) {
    const error = query.error instanceof Error ? query.error : new Error(String(query.error));
    return { status: "error", error, refetch: () => void query.refetch() };
  }
  if (query.data?.data.subscription) return { status: "active" };
  return { status: "inactive" };
}
